import { useState, useEffect, useCallback, useRef } from 'react';
import { MerchandiseOrder, InvoiceItem, MerchandiseItem } from '../types';
import { 
  getMerchandiseOrdersDB, 
  getInvoicesDB, 
  getMerchandiseItemsDB,
  saveMerchandiseOrderDB, 
  deleteMerchandiseOrderDB,
  saveMerchandiseItemDB,
  deleteMerchandiseItemDB
} from '../services/db';

// Module-level in-memory cache — shared across all component instances
let _ordersCache: MerchandiseOrder[] | null = null;
let _invoicesCache: InvoiceItem[] | null = null;
let _itemsCache: MerchandiseItem[] | null = null;
let _lastFetchTime = 0;
const CACHE_TTL_MS = 30_000; // 30 seconds before background refresh

// Subscribers for cross-component reactivity
type Listener = () => void;
const _listeners = new Set<Listener>();
function notifyAll() {
  _listeners.forEach(fn => fn());
}

/**
 * Shared hook for merchandise orders, items + invoices.
 * Uses module-level cache so switching between Pembelian ↔ Inventori is instant.
 * Instantly paints from localStorage and does background syncing with a 5s timeout safety.
 */
export function useMerchandiseData() {
  // Get initial values from memory or localStorage for instant loading
  const getInitialOrders = (): MerchandiseOrder[] => {
    if (_ordersCache) return _ordersCache;
    try {
      const local = localStorage.getItem('asmm_merchandise_orders_v1');
      if (local) {
        _ordersCache = JSON.parse(local);
        return _ordersCache!;
      }
    } catch (e) {}
    return [];
  };

  const getInitialInvoices = (): InvoiceItem[] => {
    if (_invoicesCache) return _invoicesCache;
    try {
      const local = localStorage.getItem('asmm_db_invoices_v4');
      if (local) {
        _invoicesCache = JSON.parse(local);
        return _invoicesCache!;
      }
    } catch (e) {}
    return [];
  };

  const getInitialItems = (): MerchandiseItem[] => {
    if (_itemsCache) return _itemsCache;
    try {
      const local = localStorage.getItem('asmm_db_merch_items_v1');
      if (local) {
        _itemsCache = JSON.parse(local);
        return _itemsCache!;
      }
    } catch (e) {}
    return [];
  };

  const [orders, setOrders] = useState<MerchandiseOrder[]>(getInitialOrders);
  const [invoices, setInvoices] = useState<InvoiceItem[]>(getInitialInvoices);
  const [items, setItems] = useState<MerchandiseItem[]>(getInitialItems);
  
  // loading is false if we have any cached data available
  const [isLoading, setIsLoading] = useState(() => {
    if (_ordersCache && _ordersCache.length > 0) return false;
    try {
      const local = localStorage.getItem('asmm_merchandise_orders_v1');
      return !local;
    } catch (e) {
      return true;
    }
  });

  const mountedRef = useRef(true);

  // Sync local state from module cache
  const syncFromCache = useCallback(() => {
    if (_ordersCache) setOrders(_ordersCache);
    if (_invoicesCache) setInvoices(_invoicesCache);
    if (_itemsCache) setItems(_itemsCache);
  }, []);

  // Subscribe to cross-component updates
  useEffect(() => {
    mountedRef.current = true;
    _listeners.add(syncFromCache);
    return () => {
      _listeners.delete(syncFromCache);
      mountedRef.current = false;
    };
  }, [syncFromCache]);

  // Initial fetch + background refresh
  useEffect(() => {
    const now = Date.now();
    const isCacheValid = _ordersCache !== null && _itemsCache !== null && (now - _lastFetchTime) < CACHE_TTL_MS;

    if (isCacheValid) {
      setIsLoading(false);
      return;
    }

    const runFetch = async () => {
      try {
        // 5 second timeout safety to prevent spinner hangs
        const timeoutPromise = new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('Fetch timeout')), 5000)
        );

        const fetchPromise = Promise.all([
          getMerchandiseOrdersDB(), 
          getInvoicesDB(),
          getMerchandiseItemsDB()
        ]);
        const [fetchedOrders, fetchedInvoices, fetchedItems] = await Promise.race([fetchPromise, timeoutPromise]);

        _ordersCache = fetchedOrders;
        _invoicesCache = fetchedInvoices;
        _itemsCache = fetchedItems;
        _lastFetchTime = Date.now();

        if (mountedRef.current) {
          setOrders(fetchedOrders);
          setInvoices(fetchedInvoices);
          setItems(fetchedItems);
        }
        notifyAll();
      } catch (err) {
        console.warn('Merchandise background fetch notice:', err);
      } finally {
        if (mountedRef.current) {
          setIsLoading(false);
        }
      }
    };

    runFetch();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Optimistic save order — updates cache + notifies other components instantly
  const saveOrder = useCallback(async (order: MerchandiseOrder) => {
    const updatedList = await saveMerchandiseOrderDB(order);
    _ordersCache = updatedList;
    _lastFetchTime = Date.now();
    setOrders(updatedList);
    notifyAll();
    return updatedList;
  }, []);

  // Optimistic delete order — updates cache + notifies other components instantly
  const deleteOrder = useCallback(async (orderId: string) => {
    const updatedList = await deleteMerchandiseOrderDB(orderId);
    _ordersCache = updatedList;
    _lastFetchTime = Date.now();
    setOrders(updatedList);
    notifyAll();
    return updatedList;
  }, []);

  // Optimistic save item — updates cache + notifies other components instantly
  const saveItem = useCallback(async (item: MerchandiseItem) => {
    const updatedList = await saveMerchandiseItemDB(item);
    _itemsCache = updatedList;
    _lastFetchTime = Date.now();
    setItems(updatedList);
    notifyAll();
    return updatedList;
  }, []);

  // Optimistic delete item — updates cache + notifies other components instantly
  const deleteItem = useCallback(async (itemId: string) => {
    const updatedList = await deleteMerchandiseItemDB(itemId);
    _itemsCache = updatedList;
    _lastFetchTime = Date.now();
    setItems(updatedList);
    notifyAll();
    return updatedList;
  }, []);

  // Force refresh
  const refresh = useCallback(async () => {
    setIsLoading(true);
    try {
      const [fetchedOrders, fetchedInvoices, fetchedItems] = await Promise.all([
        getMerchandiseOrdersDB(), 
        getInvoicesDB(),
        getMerchandiseItemsDB()
      ]);
      _ordersCache = fetchedOrders;
      _invoicesCache = fetchedInvoices;
      _itemsCache = fetchedItems;
      _lastFetchTime = Date.now();
      setOrders(fetchedOrders);
      setInvoices(fetchedInvoices);
      setItems(fetchedItems);
      notifyAll();
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { orders, invoices, items, isLoading, saveOrder, deleteOrder, saveItem, deleteItem, refresh };
}
